import { lazy } from 'react'
import { FIREBIRD_DB_EXTS } from '@papan/types'
import { registerEditor } from '../editor-registry.js'
import { firebirdAdapter, type DbAdapter } from '../db-adapter.js'

const LazyDatabaseEditor = lazy(() =>
  import('../../ui/DatabaseViewer.js').then(m => ({ default: m.DatabaseEditor }))
)

// Dev mock adapter — fake data with slow responses for UX testing
const delay = (ms: number) => new Promise(r => setTimeout(r, ms))
const devMockAdapter: DbAdapter = {
  type: 'firebird-mock',
  requiresAuth: true,

  async authenticate(_fileId: string, _user: string, _password: string, _rememberMe?: boolean) {
    await delay(500)
    return { ok: true }
  },

  async getSchema() {
    await delay(2000)
    return { schema: [
      { type: 'table', name: 'CUSTOMERS', sql: null, columns: [
        { name: 'ID', type: 'INTEGER', notnull: true, pk: true, defaultValue: null },
        { name: 'NAME', type: 'VARCHAR(100)', notnull: true, pk: false, defaultValue: null },
        { name: 'EMAIL', type: 'VARCHAR(200)', notnull: false, pk: false, defaultValue: null },
        { name: 'PHONE', type: 'VARCHAR(50)', notnull: false, pk: false, defaultValue: null },
        { name: 'CREATED_AT', type: 'TIMESTAMP', notnull: false, pk: false, defaultValue: null },
      ], foreignKeys: [], rowCount: 250 },
      { type: 'table', name: 'ORDERS', sql: null, columns: [
        { name: 'ID', type: 'INTEGER', notnull: true, pk: true, defaultValue: null },
        { name: 'CUSTOMER_ID', type: 'INTEGER', notnull: true, pk: false, defaultValue: null },
        { name: 'TOTAL', type: 'DECIMAL(10,2)', notnull: false, pk: false, defaultValue: null },
        { name: 'STATUS', type: 'VARCHAR(20)', notnull: false, pk: false, defaultValue: "'pending'" },
        { name: 'ORDER_DATE', type: 'DATE', notnull: false, pk: false, defaultValue: null },
      ], foreignKeys: [{ from: 'CUSTOMER_ID', table: 'CUSTOMERS', to: 'ID' }], rowCount: 1200 },
      { type: 'table', name: 'PRODUCTS', sql: null, columns: [
        { name: 'ID', type: 'INTEGER', notnull: true, pk: true, defaultValue: null },
        { name: 'NAME', type: 'VARCHAR(200)', notnull: true, pk: false, defaultValue: null },
        { name: 'PRICE', type: 'DECIMAL(10,2)', notnull: false, pk: false, defaultValue: null },
        { name: 'CATEGORY', type: 'VARCHAR(50)', notnull: false, pk: false, defaultValue: null },
      ], foreignKeys: [], rowCount: 85 },
    ]}
  },

  async getTable(_fileId, tableName, limit, offset) {
    await delay(1500)
    const rows: Record<string, unknown>[] = []
    for (let i = 0; i < Math.min(limit, 50); i++) {
      const idx = offset + i + 1
      if (tableName === 'CUSTOMERS') {
        rows.push({ __rowid: String(idx), ID: idx, NAME: `Customer ${idx}`, EMAIL: `customer${idx}@example.com`, PHONE: `+1-555-${String(idx).padStart(4, '0')}`, CREATED_AT: '2024-01-15 10:30:00' })
      } else if (tableName === 'ORDERS') {
        rows.push({ __rowid: String(idx), ID: idx, CUSTOMER_ID: (idx % 250) + 1, TOTAL: (Math.random() * 500 + 10).toFixed(2), STATUS: ['pending', 'shipped', 'delivered'][idx % 3], ORDER_DATE: '2024-03-20' })
      } else {
        rows.push({ __rowid: String(idx), ID: idx, NAME: `Product ${idx}`, PRICE: (Math.random() * 100 + 5).toFixed(2), CATEGORY: ['Electronics', 'Books', 'Clothing'][idx % 3] })
      }
    }
    const totalCount = tableName === 'CUSTOMERS' ? 250 : tableName === 'ORDERS' ? 1200 : 85
    const columns = tableName === 'CUSTOMERS' ? ['ID', 'NAME', 'EMAIL', 'PHONE', 'CREATED_AT']
      : tableName === 'ORDERS' ? ['ID', 'CUSTOMER_ID', 'TOTAL', 'STATUS', 'ORDER_DATE']
      : ['ID', 'NAME', 'PRICE', 'CATEGORY']
    return { columns, rows, totalCount, limit, offset, primaryKeys: ['ID'] }
  },

  async query(_fileId, sql) {
    await delay(1000)
    return { columns: ['RESULT'], rows: [{ RESULT: `Mock result for: ${sql}` }], rowCount: 1, duration: 42 }
  },

  async execute() { return { columns: [], rows: [], rowCount: 0, duration: 0 } },
  async cellUpdate() { return { ok: true } },
  async cellSave() { return { ok: true, results: [] } },
  // Reports the real count: the grid treats `deleted < requested` as a
  // partial failure, so a fixed 1 made every multi-row delete in dev look
  // like one and aborted the save.
  async rowDelete(_fileId: string, _table: string, rowids: number[]) {
    return { ok: true, deleted: rowids.length }
  },
  async rowInsert() { return { ok: true, rowid: 999 } },
  async tableDrop() { return { ok: true } },
  async tableCreate() { return { ok: true } },
  async tableDuplicate() { return { ok: true } },
  async tableModify() { return { ok: true } },
}

const isDev = (import.meta as any).env?.DEV

// Firebird database files — same editor as SQLite, different adapter. Every Firebird extension (the shared list, which
// the server routes and the sync passes ask too)
const FIREBIRD_EDITOR_EXTS = FIREBIRD_DB_EXTS.map((ext) => ext.slice(1))
registerEditor({
  family: 'firebird',
  extensions: [...FIREBIRD_EDITOR_EXTS],
  name: 'Database Editor (Firebird)',
  component: LazyDatabaseEditor,
  readOnly: false,
  binaryExtensions: [...FIREBIRD_EDITOR_EXTS],
  defaultProps: { adapter: isDev ? devMockAdapter : firebirdAdapter },
})
